import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { format } from "date-fns";
import { paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { currentTime } from "@shared-core/domain/current-time";
import type * as API from "@shared-core/data/api-types";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { CalendarPlus, ExternalLink } from "lucide-react";
import { useM365ConnectionStatus } from "./useM365ConnectionStatus";

export interface TicketCalendarAction {
  /** True once the ticket has an Outlook event — the action then opens it. */
  isOnCalendar: boolean;
  label: "Add to calendar" | "View in calendar";
  trigger: () => void;
  /** Render alongside the trigger: the push and "connect M365" dialogs. */
  dialogs: ReactNode;
}

/**
 * "Add to calendar" / "View in calendar" for one ticket, shared by the
 * ticket list's row menu and the ticket page. Branches on the caller's own
 * M365 connection status — connected+usable opens the push dialog,
 * otherwise the "connect Microsoft 365" prompt.
 */
export function useTicketCalendarAction(
  ticket: API.Ticket | API.TicketViewItem,
): TicketCalendarAction {
  const capabilities = usePlatformCapabilities();
  const m365Status = useM365ConnectionStatus();
  const [isCalendarInfoOpen, setCalendarInfoOpen] = useState(false);
  const [isPushDialogOpen, setPushDialogOpen] = useState(false);
  // TicketViewItem carries the composed outlook_* fields; plain Ticket
  // doesn't (see api-types.ts). A successful push updates this local
  // override immediately — the ticket prop won't reflect it until the
  // parent refetches.
  const [pushedLink, setPushedLink] = useState<{
    outlookEventId: string;
    outlookEventWebLink: string | null;
  } | null>(null);
  const outlookEventId =
    pushedLink?.outlookEventId ??
    ("outlook_event_id" in ticket ? ticket.outlook_event_id : null);
  const outlookEventWebLink =
    pushedLink?.outlookEventWebLink ??
    ("outlook_event_web_link" in ticket ? ticket.outlook_event_web_link : null);

  const trigger = () => {
    if (outlookEventId) {
      if (outlookEventWebLink)
        capabilities.openExternalUrl(outlookEventWebLink);
      return;
    }
    if (m365Status === "usable") {
      setPushDialogOpen(true);
    } else {
      setCalendarInfoOpen(true);
    }
  };

  const dialogs = (
    <>
      {isCalendarInfoOpen && (
        <CalendarSetupInfoDialog onClose={() => setCalendarInfoOpen(false)} />
      )}
      {isPushDialogOpen && (
        <PushToCalendarDialog
          ticketId={ticket.id}
          dueDate={ticket.due_date ? new Date(ticket.due_date) : null}
          onClose={() => setPushDialogOpen(false)}
          onPushed={(result) => {
            setPushedLink({
              outlookEventId: result.outlook_event_id,
              outlookEventWebLink: result.outlook_event_web_link,
            });
            setPushDialogOpen(false);
          }}
        />
      )}
    </>
  );

  return {
    isOnCalendar: Boolean(outlookEventId),
    label: outlookEventId ? "View in calendar" : "Add to calendar",
    trigger,
    dialogs,
  };
}

/** The calendar action as a standalone button, for the open ticket page. */
export function TicketCalendarButton({
  ticket,
}: {
  ticket: API.Ticket | API.TicketViewItem;
}) {
  const calendarAction = useTicketCalendarAction(ticket);
  const Icon = calendarAction.isOnCalendar ? ExternalLink : CalendarPlus;
  return (
    <>
      <Button onClick={calendarAction.trigger} variant="outline" size="sm">
        <Icon className="size-4 mr-2" />
        {calendarAction.label}
      </Button>
      {calendarAction.dialogs}
    </>
  );
}

// --- Calendar setup info dialog ---

function CalendarSetupInfoDialog({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg">Calendar Sync Not Set Up</h2>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            To add tickets to your Outlook calendar, connect Microsoft 365
            first.
          </p>
          <p className="text-sm text-muted-foreground">
            Once connected, you'll be able to push tickets directly to your
            calendar and see them alongside your other events.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Not now
          </Button>
          <Link to={paths.settingsIntegrations()} onClick={onClose}>
            <Button>Connect Microsoft 365</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// --- Push to Outlook calendar dialog ---

const DURATION_OPTIONS_MINUTES = [15, 30, 60, 90] as const;

// Defaults the dialog's start time to the ticket's own due date when it has
// one — a user who already set a due date expects "add to calendar" to use
// it, not a disconnected arbitrary time (see the calendar-test incident:
// clicking through without noticing the pre-filled field put the event an
// hour from click-time instead of the ticket's actual due date).
function defaultStartAtInputValue(dueDate: Date | null): string {
  const base = dueDate ?? new Date(currentTime().getTime() + 60 * 60 * 1000);
  return format(base, "yyyy-MM-dd'T'HH:mm");
}

function PushToCalendarDialog({
  ticketId,
  dueDate,
  onClose,
  onPushed,
}: {
  ticketId: string;
  dueDate: Date | null;
  onClose: () => void;
  onPushed: (result: API.PushTicketToCalendarResponse) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [startAt, setStartAt] = useState(() =>
    defaultStartAtInputValue(dueDate),
  );
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const parsedStart = new Date(startAt);
    if (Number.isNaN(parsedStart.getTime())) {
      setError("Pick a start date and time.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await dataAdapter.pushTicketToCalendar(ticketId, {
        start_at: parsedStart.toISOString(),
        duration_minutes: durationMinutes,
      });
      onPushed(result);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || "Couldn't push this ticket to your calendar."
          : "Couldn't push this ticket to your calendar.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg">Add to calendar</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="push-to-calendar-start">Start</Label>
            <Input
              id="push-to-calendar-start"
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {dueDate
                ? "Defaulted to this ticket's due date — check it before adding."
                : "This ticket has no due date, so defaulted to an hour from now — check it before adding."}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="push-to-calendar-duration">Duration</Label>
            <Select
              value={String(durationMinutes)}
              onValueChange={(v) => setDurationMinutes(Number(v))}
            >
              <SelectTrigger id="push-to-calendar-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS_MINUTES.map((minutes) => (
                  <SelectItem key={minutes} value={String(minutes)}>
                    {minutes} minutes
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Adding…" : "Add to calendar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
